import React, { useState, useEffect, useCallback, useRef } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
// import MainHeader from "../../Navigation/MainHeader";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Eye, Pencil, Trash2, X, Plus, MapPin, 
  Search, ChevronLeft, ChevronRight, Layers, Loader2 
} from "lucide-react";

const Plots = () => {
  const [plots, setPlots] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  
  const [inputValue, setInputValue] = useState("");
  const [filterProjectId, setFilterProjectId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [activeSearch, setActiveSearch] = useState({ term: "", projId: "", status: "" });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingPlot, setEditingPlot] = useState(null);
  const [viewPlot, setViewPlot] = useState(null);
  const [errors, setErrors] = useState({});

  const initialFormState = { 
    projectId: "", 
    plotNumber: "", 
    dimensions: "", 
    totalArea: "", 
    facing: "East", 
    plotType: "Residential", 
    plotStatus: "Available", 
    isActive: true 
  };
  
  const [formData, setFormData] = useState(initialFormState);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchProjs = async () => {
        try {
            const res = await fetch(APIConfig.APIACTIVEURL + APIConfig.PROJECT_GET_ALL + "?pageSize=100");
            const result = await res.json();
            setProjectsList(result.data || []);
        } catch (e) { console.error(e); }
    };
    fetchProjs();
  }, []);

  const fetchPlots = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.PLOT_GET_ALL}?pageNumber=${currentPage}&pageSize=10&searchTerm=${activeSearch.term}&projectId=${activeSearch.projId}&status=${activeSearch.status}`;
      const response = await fetch(url);
      const result = await response.json();
      setPlots(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [currentPage, activeSearch]);

  useEffect(() => { fetchPlots(); }, [fetchPlots]);

  // --- HANDLERS ---

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // FIX: Convert string "true"/"false" from <select> into real Boolean for the backend
    let finalValue = type === "checkbox" ? checked : value;
    if (name === "isActive") {
        finalValue = value === "true";
    }

    setFormData({ ...formData, [name]: finalValue });
    if (errors[name]) setErrors({ ...errors, [name]: false });
  };

  const handleSearchTrigger = () => {
    setActiveSearch({ term: inputValue, projId: filterProjectId, status: filterStatus });
    setCurrentPage(1);
  };

  const handleClearAll = () => {
    setInputValue(""); setFilterProjectId(""); setFilterStatus("");
    setActiveSearch({ term: "", projId: "", status: "" });
    setCurrentPage(1);
  };

  const openModal = (plot = null) => {
    setErrors({});
    if (plot) {
      setEditingPlot(plot);
      setFormData({ ...plot });
    } else {
      setEditingPlot(null);
      setFormData(initialFormState);
    }
    setShowModal(true);
  };

  const validateForm = () => {
    let tempErrors = {};
    if (!formData.projectId) tempErrors.projectId = true;
    if (!formData.plotNumber?.trim()) tempErrors.plotNumber = true;
    if (!formData.totalArea || formData.totalArea <= 0) tempErrors.totalArea = true;
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setBtnLoading(true);
    const isEdit = !!editingPlot;
    try {
      const res = await fetch(APIConfig.APIACTIVEURL + APIConfig.PLOT_BASE, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setShowModal(false);
        fetchPlots();
        Swal.fire({ icon: 'success', title: isEdit ? 'Updated' : 'Created', timer: 1500, showConfirmButton: false });
      } else {
        // --- NEW: Detailed Server Error Handling ---
        const errorData = await res.json();
        let msg = "The server rejected this update.";
        if (errorData.errors) msg = Object.values(errorData.errors).flat().join("<br/>");
        Swal.fire({ icon: 'error', title: 'Update Failed', html: msg });
      }
    } catch (err) { Swal.fire('Error', 'Network connection failed.', 'error'); }
    finally { setBtnLoading(false); }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Delete Plot?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Yes, Delete'
    }).then(async (result) => {
      if (result.isConfirmed) {
        await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PLOT_BASE}/${id}`, { method: "DELETE" });
        fetchPlots();
        Swal.fire('Deleted!', '', 'success');
      }
    });
  };

  const styles = {
    container: { padding: "40px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    card: { backgroundColor: "#fff", borderRadius: "20px", boxShadow: "0 4px 25px rgba(0,0,0,0.05)", overflow: "hidden" },
    filterInput: { padding: "10px 12px", borderRadius: "10px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "13px", color: "#475569" },
    searchBtn: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "11px 25px", borderRadius: "10px", cursor: "pointer", fontWeight: "700" },
    addBtn: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "12px 24px", borderRadius: "12px", cursor: "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" },
    modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2000, backdropFilter: "blur(6px)" },
    modalContent: { backgroundColor: "#fff", padding: "35px", borderRadius: "28px", width: "580px", boxShadow: "0 30px 60px rgba(0,0,0,0.15)" },
    formField: (err) => ({ width: '100%', padding: '13px', borderRadius: '12px', border: err ? '2px solid #ef4444' : '1.5px solid #e2e8f0', marginTop: '6px', outline: 'none', boxSizing: 'border-box', fontSize: '14px' }),
    badge: (status) => ({
        padding: "5px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "700",
        backgroundColor: status === "Available" ? "#ecfdf5" : status === "Sold" ? "#fef2f2" : "#fffbeb",
        color: status === "Available" ? "#059669" : status === "Sold" ? "#ef4444" : "#d97706"
    })
  };

  return (
    <>
      {/* <MainHeader /> */}
       <Navbar /> 
      <div style={styles.container}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
            <h2 style={{ fontSize: "28px", fontWeight: "800", color: "#1b2559", margin: 0 }}>Plots</h2>
            <button style={styles.addBtn} onClick={() => openModal()}>
               <Plus size={18} /> Add New Plot
            </button>
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "25px", flexWrap: "wrap" }}>
            <div style={{ position: "relative", width: "250px" }}>
                <Search size={16} style={{ position: "absolute", left: "12px", top: "12px", color: "#94a3b8" }} />
                <input style={{ ...styles.filterInput, width: '100%', paddingLeft: '35px', boxSizing: 'border-box' }} placeholder="Search Plot No..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
            </div>
            <select style={{...styles.filterInput, minWidth: '160px'}} value={filterProjectId} onChange={(e) => setFilterProjectId(e.target.value)}>
                <option value="">All Projects</option>
                {projectsList.map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
            </select>
            <select style={{...styles.filterInput, minWidth: '140px'}} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="Available">Available</option><option value="Booked">Booked</option><option value="Sold">Sold</option><option value="Blocked">Blocked</option>
            </select>
            <button onClick={handleSearchTrigger} style={styles.searchBtn}>Search</button>
            {(inputValue || filterProjectId || filterStatus) && (
                <button onClick={handleClearAll} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:'13px', fontWeight:'600' }}>Clear All</button>
            )}
        </div>

        <div style={styles.card}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc", color: "#a3aed0", fontSize: "12px", textTransform: "uppercase", fontWeight: "700" }}>
                <th style={{ padding: "18px 20px", textAlign: "left" }}>Plot Number</th>
                <th style={{ padding: "18px 20px", textAlign: "left" }}>Project Site</th>
                <th style={{ padding: "18px 20px", textAlign: "left" }}>Area</th>
                <th style={{ padding: "18px 20px", textAlign: "left" }}>Status</th>
                <th style={{ padding: "18px 20px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="5" style={{ padding: "60px", textAlign: "center" }}>Loading Inventory...</td></tr> : 
               plots.map((p) => (
                <tr key={p.plotId} style={{ borderBottom: "1px solid #f4f7fe" }}>
                  <td style={{ padding: "20px", fontWeight: "700", color: "#1b2559" }}>{p.plotNumber} <div style={{fontSize:'10px', color:'#a3aed0'}}>{p.facing} Facing</div></td>
                  <td style={{ padding: "20px", fontSize: "14px" }}><MapPin size={12}/> {p.project?.projectName}</td>
                  <td style={{ padding: "20px", fontSize: "14px" }}>{p.totalArea} SQYD</td>
                  <td style={{ padding: "20px" }}><span style={styles.badge(p.plotStatus)}>{p.plotStatus}</span></td>
                  <td style={{ padding: "20px", textAlign: "center" }}>
                    <div style={{ display:'flex', justifyContent:'center', gap:'10px' }}>
                        <button onClick={() => { setViewPlot(p); setShowViewModal(true); }} style={{ border:'none', background:'#eff6ff', color:'#3b82f6', padding:'8px', borderRadius:'8px', cursor:'pointer' }}><Eye size={18}/></button>
                        <button onClick={() => openModal(p)} style={{ border:'none', background:'#eef2ff', color:'#6366f1', padding:'8px', borderRadius:'8px', cursor:'pointer' }}><Pencil size={18}/></button>
                        <button onClick={() => handleDelete(p.plotId)} style={{ border:'none', background:'#fef2f2', color:'#ef4444', padding:'8px', borderRadius:'8px', cursor:'pointer' }}><Trash2 size={18}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #f4f7fe" }}>
            <span style={{ fontSize: "14px", color: "#a3aed0" }}>Page {currentPage} of {totalPages}</span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={{ padding: "8px 15px", borderRadius: "10px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer" }}><ChevronLeft size={18}/></button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => c + 1)} style={{ padding: "8px 15px", borderRadius: "10px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer" }}><ChevronRight size={18}/></button>
            </div>
          </div>
        </div>

        {/* MODAL: ADD / EDIT */}
        {showModal && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'25px' }}>
                 <h3 style={{ margin:0, fontWeight:'800', color: "#1b2559" }}>{editingPlot ? "Update Plot" : "New Plot Registration"}</h3>
                 <X size={22} style={{ cursor:'pointer', color:'#a3aed0' }} onClick={() => setShowModal(false)} />
              </div>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}><label style={{ fontSize: '13px', fontWeight: '700' }}>Assign Project Site</label>
                  <select name="projectId" style={styles.formField(errors.projectId)} value={formData.projectId} onChange={handleInputChange}>
                    <option value="">-- Choose Project Site --</option>
                    {projectsList.map(proj => <option key={proj.projectId} value={proj.projectId}>{proj.projectName}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '25px', marginBottom: '20px' }}>
                    <div style={{ flex: 1 }}><label style={{ fontSize: '13px', fontWeight: '700' }}>Plot Number</label>
                        <input name="plotNumber" placeholder="e.g. MBNR-101" style={styles.formField(errors.plotNumber)} value={formData.plotNumber} onChange={handleInputChange} />
                    </div>
                    <div style={{ flex: 1 }}><label style={{ fontSize: '13px', fontWeight: '700' }}>Facing</label>
                        <select name="facing" style={styles.formField(false)} value={formData.facing} onChange={handleInputChange}>
                            <option value="East">East</option><option value="West">West</option><option value="North">North</option><option value="South">South</option><option value="North-East">North-East</option>
                        </select>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '25px', marginBottom: '20px' }}>
                    <div style={{ flex: 1 }}><label style={{ fontSize: '13px', fontWeight: '700' }}>Area Size (SqYd)</label>
                        <input type="number" step="0.000001" name="totalArea" style={styles.formField(errors.totalArea)} value={formData.totalArea} onChange={handleInputChange} />
                    </div>
                    <div style={{ flex: 1 }}><label style={{ fontSize: '13px', fontWeight: '700' }}>Sales Status</label>
                        <select name="plotStatus" style={styles.formField(false)} value={formData.plotStatus} onChange={handleInputChange}>
                            <option value="Available">Available</option><option value="Booked">Booked</option><option value="Sold">Sold</option><option value="Blocked">Blocked</option>
                        </select>
                    </div>
                </div>
                <div style={{ marginBottom: '35px' }}><label style={{ fontSize: '13px', fontWeight: '700' }}>Admin Control</label>
                   <select name="isActive" style={styles.formField(false)} value={formData.isActive} onChange={handleInputChange}>
                       <option value={true}>Active (Visible to Public)</option>
                       <option value={false}>Inactive (Hidden)</option>
                   </select>
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end', gap:'15px' }}>
                   <button type="button" onClick={() => setShowModal(false)} style={{ padding:'12px 25px', borderRadius:'12px', border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontWeight:'700', color:'#a3aed0' }}>Cancel</button>
                   <button type="submit" style={{ backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "12px 35px", borderRadius: "12px", cursor: "pointer", fontWeight: "700", display:'flex', alignItems:'center', gap:'8px' }} disabled={btnLoading}>
                       {btnLoading && <Loader2 className="animate-spin" size={18} />} Save Plot Info
                   </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL: VIEW DETAILS */}
        {showViewModal && viewPlot && (
          <div style={styles.modalOverlay}>
            <div style={{ ...styles.modalContent, borderTop:'6px solid #4f46e5' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'25px' }}>
                    <h3 style={{ margin:0, fontWeight:'800', color: "#1b2559" }}>Plot Details</h3>
                    <X size={22} style={{ cursor:'pointer', color:'#a3aed0' }} onClick={() => setShowViewModal(false)} />
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'25px' }}>
                    <div style={{ gridColumn:'span 2' }}><small style={{ color:'#a3aed0', fontWeight:'700' }}>PLOT IDENTITY</small><div style={{ fontSize:'20px', fontWeight:'800', color: "#1b2559" }}>{viewPlot.plotNumber}</div></div>
                    <div style={{ gridColumn:'span 2' }}><small style={{ color:'#a3aed0', fontWeight:'700' }}>PROJECT SITE</small><div style={{ fontWeight:'600', color:'#4f46e5' }}>{viewPlot.project?.projectName}</div></div>
                    <div><small style={{ color:'#a3aed0', fontWeight:'700'}}>MEASUREMENT</small><div>{viewPlot.totalArea} SqYd</div></div>
                    <div><small style={{ color:'#a3aed0', fontWeight:'700'}}>DIRECTION</small><div>{viewPlot.facing}</div></div>
                    <div><small style={{ color:'#a3aed0', fontWeight:'700'}}>AVAILABILITY</small><div style={{ marginTop:'5px' }}><span style={styles.badge(viewPlot.plotStatus)}>{viewPlot.plotStatus}</span></div></div>
                </div>
                <div style={{ marginTop:'40px', textAlign:'right' }}><button onClick={() => setShowViewModal(false)} style={{ backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "10px 30px", borderRadius: "12px", cursor: "pointer", fontWeight: "700" }}>Close View</button></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Plots;